import React from 'react';
import styled from '@emotion/styled';
import PropTypes, { InferProps } from 'prop-types';
import { Grid, GridCell } from '../Grid';
import Headline4 from '../Typography/Heading/Headline4';
import { Breadcrumbs } from '../Breadcrumb';
import InnerHtmlStyling from '../InnerHtmlStyling';
import breakpoints from '../Theme/breakpoints';
import { mediumRoundedCorners } from '../RoundedCorners';

const FloatingButton = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 36px;
`;

const Details = styled.dl`
  display: flex;
  flex-wrap: wrap;

  dt, dd {
    font-family: ${({ theme }) => theme.fonts.primary};
    padding-bottom: 16px;
    font-size: ${({ theme }) => theme.sizes.body1};
  }

  dt {
    width: 40%
  }

  dd {
    width: 60%;
    margin: 0;
    color: ${({ theme }) => theme.colors.blackHigh};
  }
`;

const detailPropertyTypes = {
  detailName: PropTypes.string,
  detailValue: PropTypes.node,
};

type DetailProperties = InferProps<typeof detailPropertyTypes>;

const ProductDescription = styled(InnerHtmlStyling)`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.sizes.body1};
`;

const Detail = ({ detailName, detailValue }: DetailProperties) => (
  <>
    <dt>
      {detailName}
      :
    </dt>
    <dd>{detailValue}</dd>
  </>
);

Detail.propTypes = detailPropertyTypes;

const ProductDetailWrapper = styled.div`
  max-width: 1416px;
  && {
    margin: 0 auto;
    @media (min-width: ${breakpoints.sm}px) {
      padding: 0 0 24px;
    }
  }
  display: flex;
  flex-wrap: wrap;
  justify-content: start;
`;

const ProductBreadcrumbsWrapper = styled.div`
  display: flex;
  justify-content: start;
  flex-basis: 100%;
  padding-left: 8px;
`;

const ProductHeadline = styled(Headline4)`
  && {
    margin: 0 8px;
  }
  order: 1;
  flex: 1 0 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  height: calc(100vh - 84px);
  word-break: break-word;
  @media (min-width: ${breakpoints.sm}px) {
    height: auto;
    flex: 1 0 calc(100% - 16px);
    && {
      margin: 20px 8px 40px;
    }
  }
`;

const ProductImageSetWrapper = styled.div`
  flex: 1 0 100%;
  justify-content: center;
  display: inline-flex;
  flex-wrap: wrap;
  position: relative;
  order: 2;

  @media (min-width: ${breakpoints.sm}px) {
    order: 3;
  }

  @media (min-width: ${breakpoints.md}px) {
    display: flex;
    justify-content: right;
    margin: 0 12px 0 0;
    flex: 2 0 calc(62% - 12px);
  }
`;

const AnchorContainer = styled.div`
  flex: 1 0 calc(100% - 12px);
  text-transform: uppercase;
  font-family: ${({ theme }) => theme.fonts.secondary};
  padding: 16px 0 24px;
  order: 3;

  @media (min-width: ${breakpoints.sm}px) {
    order: 2;
    padding: 6px 0 6px 6px;
  }

  a {
    color: ${({ theme }) => theme.colors.blackMedium};
  }

  svg {
    margin-right: 6px;
    margin-bottom: -6px;
  }
`;

const ProductDetailPanel = styled.div`
  flex: 1 0 100%;
  margin: 0 auto;
  max-width: none;
  order: 4;

  && {
    @media (min-width: ${breakpoints.md}px) {
      flex: 1 0 38%;
    }
  }
`;

export const ProductImageWrapper = styled.div`
  max-width: 100%;
  height: auto;

  /* stylelint-disable-next-line selector-type-no-unknown */
  && progressive-img, img {
    width: calc(100vw - 24px);
    --img-width: 100%;
    margin: 0;
  }

  & > div {
    overflow: hidden;
    ${mediumRoundedCorners};
  }

  @media (min-width: ${breakpoints.md}px) {
    padding: 0 8px;
    flex: 1 0 445px;

    & > div {
      display: flex;
      justify-content: center;
    }

    /* stylelint-disable-next-line selector-type-no-unknown */
    && progressive-img, img {
      width: inherit;
      max-width: 864px;
    }
  }
`;

export const ProductLinkWrapper = styled.span`
  a {
    color: #0000EE;
    text-decoration: underline;
  }
`;

const productPropertyTypes = {
  title: PropTypes.string,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
    ]),
  ).isRequired,
  hasBreadCrumbs: PropTypes.bool,
  body: PropTypes.string,
  images: PropTypes.node,
  button: PropTypes.node,
  backToOverview: PropTypes.node,
  details: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.node,
  }).isRequired),
};

type ProductProperties = InferProps<typeof productPropertyTypes>;

function Product({
  title,
  breadcrumbs,
  body,
  images,
  button,
  backToOverview,
  details,
  hasBreadCrumbs = true,
}: ProductProperties) {
  return (
    <Grid spacing={6}>
      <GridCell columns={12} padding="0 12px 20px">
        <ProductDetailWrapper>
          {hasBreadCrumbs
          && (
          <ProductBreadcrumbsWrapper>
            <Breadcrumbs crumbs={breadcrumbs} />
          </ProductBreadcrumbsWrapper>
          )}
          <ProductHeadline tag="h1">{title}</ProductHeadline>
          <ProductImageSetWrapper>
            {images}
          </ProductImageSetWrapper>
          <AnchorContainer>
            {backToOverview}
          </AnchorContainer>
          <ProductDetailPanel>
            <ProductDescription dangerouslySetInnerHTML={{ __html: body }} />
            <Details>
              {details?.filter(({ value }) => value)
                .map(({ name, value }) => (
                  <Detail
                    detailName={name}
                    detailValue={value}
                    key={name}
                  />
                ))}
            </Details>
            <FloatingButton>
              {button}
            </FloatingButton>
          </ProductDetailPanel>
        </ProductDetailWrapper>
      </GridCell>
    </Grid>
  );
}

Product.propTypes = productPropertyTypes;

export default Product;
